import React from 'react';

import cx from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';

import Modal from 'components/Modal/Modal';
import SpinnerRing from 'components/SpinnerRing';

import StatusPill from './StatusPill';
import styles from '../DbSnapshots.module.scss';

const renderMetaValue = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const SnapshotDetailDrawer = ({ isOpen, onClose, snapshot, isLoading }) => (
  <Modal
    title='Snapshot details'
    isOpen={isOpen}
    onRequestClose={onClose}
    shouldCloseOnOverlayClick
    direction='ltr'
    styles={{ minWidth: 540 }}
  >
    {isLoading || !snapshot ? (
      <SpinnerRing />
    ) : (
      <div className={cx(styles.drawerBody, 'p-3 d-flex flex-column gap-3')}>
        <header className='d-flex align-items-center justify-content-between'>
          <div>
            <h4 className='m-0'>{snapshot.tenantId}</h4>
            <p className='text-gray-500 font-size-12'>Job ID: {snapshot.id}</p>
          </div>
          <StatusPill status={snapshot.status} />
        </header>

        <section>
          <h6 className='mb-2'>Request</h6>
          <p>
            <span className='text-gray-500'>Requested by:</span> {snapshot.requestedBy || '—'}
          </p>
          <p>
            <span className='text-gray-500'>Reason:</span> {snapshot.reason || '—'}
          </p>
          <p>
            <span className='text-gray-500'>Created:</span>{' '}
            {snapshot.createdAt ? moment(snapshot.createdAt).format('YYYY-MM-DD HH:mm:ss') : '—'}
          </p>
          {snapshot.expiresAt && (
            <p>
              <span className='text-gray-500'>Expires:</span> {moment(snapshot.expiresAt).format('YYYY-MM-DD HH:mm:ss')}
            </p>
          )}
          {snapshot.failureReason && (
            <p className='text-danger'>
              <span className='text-gray-500'>Failure reason:</span> {snapshot.failureReason}
            </p>
          )}
        </section>

        <section>
          <h6 className='mb-2'>Event timeline</h6>
          {(!snapshot.events || snapshot.events.length === 0) && (
            <p className='text-gray-500'>No events recorded yet.</p>
          )}
          <ul className={styles.timeline} data-testid='snapshot-timeline'>
            {(snapshot.events || []).map((event) => (
              <li key={event.id} className={styles.timelineItem}>
                <div className={styles.timelineDot} />
                <div className={styles.timelineContent}>
                  <p className='font-weight-bold m-0'>{event.label || event.type}</p>
                  <p className='text-gray-500 font-size-12 m-0'>
                    {event.occurredAt ? moment(event.occurredAt).format('YYYY-MM-DD HH:mm:ss') : '—'}
                    {event.actor && <span className='ms-2'>by {event.actor}</span>}
                  </p>
                  {event.metadata && (
                    <dl className={styles.metadata}>
                      {Object.entries(event.metadata).map(([key, value]) => (
                        <React.Fragment key={key}>
                          <dt>{key}</dt>
                          <dd>{renderMetaValue(value)}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    )}
  </Modal>
);

SnapshotDetailDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  snapshot: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tenantId: PropTypes.string,
    status: PropTypes.string,
    reason: PropTypes.string,
    failureReason: PropTypes.string,
    createdAt: PropTypes.string,
    expiresAt: PropTypes.string,
    requestedBy: PropTypes.string,
    events: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        type: PropTypes.string,
        label: PropTypes.string,
        occurredAt: PropTypes.string,
        actor: PropTypes.string,
        metadata: PropTypes.object,
      })
    ),
  }),
  isLoading: PropTypes.bool,
};

SnapshotDetailDrawer.defaultProps = {
  snapshot: null,
  isLoading: false,
};

export default SnapshotDetailDrawer;
